/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Modal,
  Spin,
  message,
  Typography,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Table from 'components/Table';
import Select from 'components/Select';
import { ActionsNames, hasActionPermission } from 'utils/permission/index';
import { isMutable } from 'utils/estadosGlobales';
import {
  deleteClasificadorDeTiposDeCargos,
  getClasificadoresDeTiposDeCargo,
  patchClasificadorDeTiposDeCargos,
  postClasificadorDeTiposDeCargos,
} from 'api/recaudacion/clasificadorDeTiposDeCargo';
import { getCatalogoDePadrones } from 'api/catalogos';
import { onError } from 'utils/handlers';

const { Text } = Typography;
const catalogsModels = ['rubro', 'tipo'];

const ClasificadoresDeTiposDeCargos = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [mutable, setMutable] = useState(true);
  const [padrones, setPadrones] = useState([]);
  const [clasificadorValue, setClasificadorValue] = useState('');

  const fetchData = async () => {
    setLoading(true);
    const res = await getClasificadoresDeTiposDeCargo(null, false);
    setData(res);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    let hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = catalogsModels
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      hasAllPermission = _hasAllViewPermission;
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const _data = await getClasificadoresDeTiposDeCargo();
      const _padrones = await getCatalogoDePadrones();
      if (mounted) {
        setData(_data);
        setPadrones(_padrones);
        setLoading(false);
      }
    };
    validatePermission();
    if (hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
    setClasificadorValue('');
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key ? patchClasificadorDeTiposDeCargos(key, values)
        : postClasificadorDeTiposDeCargos(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteClasificadorDeTiposDeCargos(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = () => {
    const match = data.find((d) => d.id === selectedRowKeys[0]);
    if (match.clasificador.includes('.')) {
      const fatherClasiffier = match.clasificador.split('.')[0];
      const father = data.find((e) => e.clasificador === fatherClasiffier);
      form.setFieldsValue({ clasificador_padre: father.id });
    }
    setClasificadorValue(match.clasificador || '');
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clasificador',
      dataIndex: 'clasificador',
      key: 'clasificador',
      width: 150,
    },
    {
      titleText: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 550,
    },
  ];

  const rules = {
    required: [{ required: true }], // ^[0-9]{2}|.[0-9]{2}$
    clasificador: [{
      validator: async (_, value) => {
        if (/^[0-9]{2}(|.[0-9]{2})$/.test(value)) {
          const classifier = value.includes('.') ? value.split('.')[0] : value;
          const subclassifier = value.includes('.') ? parseInt(value.split('.')[1], 10) : null;
          const padronesIds = padrones.map((p) => p.id);
          const match = data.filter((item) => !item.clasificador.includes('.'))
            .find((item) => item.clasificador === classifier);
          if (value.includes('.') && classifier !== '03' && subclassifier === 5) {
            throw new Error('El subclasificador "05" solo es válido para el clasificador "03"');
          }
          if (value.includes('.') && value.split('.').length > 2) {
            throw new Error('Solo se permite un punto en el clasificador ("clasificador padre"."padron id")');
          }
          if (subclassifier && !padronesIds.includes(subclassifier)) {
            throw new Error('El subclasificador no se encuentra entre la lista de padrones.');
          }
          if (match && value.includes('.')) {
            form.setFieldsValue({ clasificador_padre: match.id });
          } else {
            form.setFieldsValue({ clasificador_padre: null });
          }
        } else {
          throw new Error('El clasificador no cumple con el formato requerido (00 o 00.00)');
        }
      },
    }],
    clasificador_padre: [{
      validator: async (_, value) => {
        const clasificador = form.getFieldValue('clasificador') || '';
        if (!value && clasificador.includes('.')) {
          throw new Error('Este campo es requerido');
        }
      },
    }],
  };

  const getPadronFromClasificador = () => {
    if (clasificadorValue.includes('.')) {
      const padron = clasificadorValue.split('.')[1];
      const _padron = padrones
        .find((p) => p.id === parseInt(padron, 10))?.descripcion;
      return _padron ? `Subclasificación del padrón ${_padron}` : 'No existe';
    }
    return '';
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          rowKey="id"
          permission={permission}
          hasAllViewPermission={hasAllViewPermission}
        />
        <Modal
          visible={visible}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Clasificador`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          footer={null}
          closable={false}
          keyboard={!loading}
          maskClosable={!loading}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            onValuesChange={(_, values) => {
              setClasificadorValue(values.clasificador || '');
            }}
          >
            <Row>
              {clasificadorValue?.includes('.') && (
                <Col span={24}>
                  <Text>
                    {getPadronFromClasificador()}
                  </Text>
                </Col>
              )}
              <Col span={24}>
                <Form.Item
                  name="clasificador"
                  label="Clasificador"
                  rules={rules.clasificador}
                >
                  <Input
                    disabled={!mutable}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="clasificador_padre"
                  label="Clasificador Padre"
                  rules={rules.clasificador_padre}
                >
                  <Select
                    dataSource={data.filter((i) => !i.clasificador.includes('.'))}
                    labelProp="nombre"
                    onChange={(val) => {
                      const match = data.find((i) => i.id === val);
                      if (match) {
                        form.setFieldsValue({ clasificador: `${match.clasificador}.` });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="nombre"
                  label="Nombre"
                  rules={rules.required}
                  hasFeedback
                >
                  <Input allowClear disabled={!mutable} />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            onCancel();
          }}
          visible={visibleAlert}
          content={` el clasificador ${form.getFieldValue('nombre')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ClasificadoresDeTiposDeCargos;
